import React, { useEffect, useState, useContext } from "react";
import { ID } from "@refinedev/appwrite";
import { database } from "utility/appwriteClient";
import { TentantContext } from "contexts/tentant";
import { Query } from "@refinedev/appwrite";

import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
} from "@mui/material";

import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { List } from "@refinedev/mui";

interface Package {
  $id: string;
  Package_Name: string;
  Package_Type: string;
  Package_Length: number;
  Package_Width: number;
  Package_Height: number;
}

const PackagePage: React.FC = () => {
  const [tentant] = useContext(TentantContext);
  const [packages, setPackages] = useState<Package[]>([]);
  const [newPackage, setNewPackage] = useState<Package>({
    $id: "",
    Package_Type: "",
    Package_Name: "",
    Package_Length: 0,
    Package_Width: 0,
    Package_Height: 0,
  });
  const [isPackageValid, setIsPackageValid] = useState(false);
  const [isEditPackageValid, setIsEditPackageValid] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [selectedPackageToDelete, setSelectedPackageToDelete] = useState<string | null>(null);

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    validatePackage();
  }, [newPackage]);

  useEffect(() => {
    validateEditPackage(selectedPackage);
  }, [selectedPackage]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      let results: Package[] = [];
      let page = 0;
      let hasMore = true;

      while (hasMore) {
        const response = await database.listDocuments(
          tentant.toLowerCase(),
          tentant.toLowerCase() + "_preset",
          [Query.limit(1000), Query.offset(page * 1000)]
        );

        results.push(...(response.documents as unknown as Package[]));
        hasMore = response.documents.length === 1000;
        page++;
      }
      setPackages(results);
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  };

  const rows = packages.map((document) => ({
    id: document.$id,
    name: document.Package_Name,
    packageType: document.Package_Type,
    packageLength: document.Package_Length,
    packageWidth: document.Package_Width,
    packageHeight: document.Package_Height,
  }));

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      filterable: true,
    },
    {
      field: "packageType",
      headerName: "Type",
      flex: 1,
      filterable: true,
    },
    {
      field: "packageLength",
      headerName: "Length",
      flex: 1,
      filterable: true,
    },
    {
      field: "packageWidth",
      headerName: "Width",
      flex: 1,
      filterable: true,
    },
    {
      field: "packageHeight",
      headerName: "Height",
      flex: 1,
      filterable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      width: 180,
      renderCell: (params) => {
        const pkg = packages.find((p) => p.$id === params.row.id);
        return (
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (pkg) {
                  setSelectedPackage(pkg);
                  setOpenEditModal(true);
                }
              }}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPackageToDelete(params.row.id);
                setOpenDeleteModal(true);
              }}
            >
              Delete
            </Button>
          </Stack>
        );
      },
    },
  ];

  const addPackage = async () => {
    try {
      await database.createDocument(
        tentant.toLowerCase(),
        tentant.toLowerCase() + "_preset",
        ID.unique(),
        {
          Package_Name: newPackage.Package_Name,
          Package_Type: newPackage.Package_Type,
          Package_Length: newPackage.Package_Length.toString(),
          Package_Width: newPackage.Package_Width.toString(),
          Package_Height: newPackage.Package_Height.toString(),
        }
      );
      setNewPackage({
        $id: "",
        Package_Name: "",
        Package_Type: "",
        Package_Length: 0,
        Package_Width: 0,
        Package_Height: 0,
      });
      setIsPackageValid(false);
      fetchPackages();
    } catch (error) {
      console.error("Error saving packages:", error);
    }
  };

  const updatePackage = async (pkg: Package) => {
    try {
      await database.updateDocument(
        tentant.toLowerCase(),
        tentant.toLowerCase() + "_preset",
        pkg.$id,
        {
          Package_Name: pkg.Package_Name,
          Package_Type: pkg.Package_Type,
          Package_Length: pkg.Package_Length.toString(),
          Package_Width: pkg.Package_Width.toString(),
          Package_Height: pkg.Package_Height.toString(),
        }
      );
      fetchPackages();
    } catch (error) {
      console.error("Error updating package:", error);
    }
  };

  const deletePackage = async (packageId: string) => {
    try {
      await database.deleteDocument(
        tentant.toLowerCase(),
        tentant.toLowerCase() + "_preset",
        packageId
      );
      fetchPackages();
    } catch (error) {
      console.error("Error deleting package:", error);
    }
  };

  const resetAddPackageModal = () => {
    setNewPackage({
      $id: "",
      Package_Name: "",
      Package_Type: "",
      Package_Length: 0,
      Package_Width: 0,
      Package_Height: 0,
    });
    setIsPackageValid(false);
    setOpenAddModal(false);
  };

  const validatePackage = () => {
    if (
      newPackage.Package_Name.trim() === "" ||
      newPackage.Package_Name.trim() === "" ||
      newPackage.Package_Type.trim() === "" ||
      newPackage.Package_Length === 0 ||
      newPackage.Package_Width === 0 ||
      newPackage.Package_Height === 0
    ) {
      setIsPackageValid(false);
    } else {
      setIsPackageValid(true);
    }
  };

  const validateEditPackage = (pkg: Package | null) => {
    if (
      pkg &&
      pkg.Package_Name.trim() !== "" &&
      pkg.Package_Type.trim() !== "" &&
      pkg.Package_Length > 0 &&
      pkg.Package_Width > 0 &&
      pkg.Package_Height > 0
    ) {
      setIsEditPackageValid(true);
    } else {
      setIsEditPackageValid(false);
    }
  };

  return (
    <Box sx={{ p: 4, borderRadius: 4, backgroundColor: "background.paper" }}>
      <Grid container>
        <Grid item xs={12} lg={12}>
          <List
            wrapperProps={{
              sx: {
                backgroundImage:
                  "linear-gradient(rgba(255, 255, 255, 0.00), rgba(255, 255, 255, 0.00))",
              },
            }}
            headerProps={{
              action: (
                <Stack direction="row" alignItems="center">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenAddModal(true)}
                  >
                    Add New Package
                  </Button>
                </Stack>
              ),
            }}
          ></List>
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[10, 50, 100]}
          />
        </Grid>
      </Grid>
      <Dialog
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Package</DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Package Name"
              value={newPackage.Package_Name}
              onChange={(e) =>
                setNewPackage({ ...newPackage, Package_Name: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Package Type"
              value={newPackage.Package_Type}
              onChange={(e) =>
                setNewPackage({ ...newPackage, Package_Type: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Length"
              type="number"
              value={newPackage.Package_Length}
              onChange={(e) =>
                setNewPackage({
                  ...newPackage,
                  Package_Length: parseInt(e.target.value),
                })
              }
              fullWidth
            />
            <TextField
              label="Width"
              type="number"
              value={newPackage.Package_Width}
              onChange={(e) =>
                setNewPackage({
                  ...newPackage,
                  Package_Width: parseInt(e.target.value),
                })
              }
              fullWidth
            />
            <TextField
              label="Height"
              type="number"
              value={newPackage.Package_Height}
              onChange={(e) =>
                setNewPackage({
                  ...newPackage,
                  Package_Height: parseInt(e.target.value),
                })
              }
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            color="primary"
            onClick={resetAddPackageModal}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await addPackage();
              resetAddPackageModal();
              fetchPackages();
            }}
            variant="contained"
            color="primary"
            disabled={!isPackageValid}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          setSelectedPackage(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Package</DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          {selectedPackage && (
            <Stack spacing={2} mt={1}>
              <TextField
                label="Package Name"
                value={selectedPackage.Package_Name}
                onChange={(e) =>
                  setSelectedPackage({
                    ...selectedPackage,
                    Package_Name: e.target.value,
                  })
                }
                fullWidth
              />
              <TextField
                label="Package Type"
                value={selectedPackage.Package_Type}
                onChange={(e) =>
                  setSelectedPackage({
                    ...selectedPackage,
                    Package_Type: e.target.value,
                  })
                }
                fullWidth
              />
              <TextField
                label="Length"
                type="number"
                value={selectedPackage.Package_Length}
                onChange={(e) =>
                  setSelectedPackage({
                    ...selectedPackage,
                    Package_Length: parseInt(e.target.value),
                  })
                }
                fullWidth
              />
              <TextField
                label="Width"
                type="number"
                value={selectedPackage.Package_Width}
                onChange={(e) =>
                  setSelectedPackage({
                    ...selectedPackage,
                    Package_Width: parseInt(e.target.value),
                  })
                }
                fullWidth
              />
              <TextField
                label="Height"
                type="number"
                value={selectedPackage.Package_Height}
                onChange={(e) =>
                  setSelectedPackage({
                    ...selectedPackage,
                    Package_Height: parseInt(e.target.value),
                  })
                }
                fullWidth
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setOpenEditModal(false);
              setSelectedPackage(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (selectedPackage) {
                await updatePackage(selectedPackage);
                setOpenEditModal(false);
                setSelectedPackage(null);
              }
            }}
            variant="contained"
            color="primary"
            disabled={!isEditPackageValid}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDeleteModal}
        onClose={() => {
          setOpenDeleteModal(false);
          setSelectedPackageToDelete(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          Are you sure you want to delete this package? This action cannot be
          undone.
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setOpenDeleteModal(false);
              setSelectedPackageToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (selectedPackageToDelete) {
                await deletePackage(selectedPackageToDelete);
                setOpenDeleteModal(false);
                setSelectedPackageToDelete(null);
              }
            }}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Backdrop open={loading} sx={{ zIndex: 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default PackagePage;
